import React, { useEffect, useRef, useState } from "react";
import TextField from "./inputs/TextField";
import SelectInput from "./inputs/SelectInput";
import RadioInput from "./inputs/RadioInput";
import FileInput from "./inputs/FileInput";
import TextareaInput from "./inputs/TextareaInput";
import InfoCard from "./inputs/InfoCard";

const INPUT_MAP = {
  text: TextField,
  email: TextField,
  phone: TextField,
  select: SelectInput,
  radio: RadioInput,
  file: FileInput,
  textarea: TextareaInput,
};

/* ── helpers ──────────────────────────────────────────────────────────────── */

function formatAnswer(question, value) {
  if (value === undefined || value === null || value === "") return null;

  switch (question.type) {
    case "radio": {
      const opt = question.options?.find((o) => o.value === value);
      return opt?.label || value;
    }
    case "select": {
      const opt = question.options?.find((o) => o.value === value);
      return opt?.label || value;
    }
    case "file": {
      return value.name || "File terunggah";
    }
    case "textarea":
    case "text":
    default:
      if (typeof value === "string") {
        return value.length > 120 ? value.slice(0, 120) + "\u2026" : value;
      }
      return String(value);
  }
}

/* ── component ────────────────────────────────────────────────────────────── */

const QuestionCard = React.memo(function QuestionCard({
  question,
  value,
  onChange,
  error,
  isActive,
  isRevealed,
  questionNumber,
  onBack,
  onNext,
  onSubmit,
  onEdit,
  isFirst,
  isLast,
  submitLabel,
}) {
  const cardRef = useRef(null);
  const wasRevealedRef = useRef(false);
  const [isEntering, setIsEntering] = useState(false);

  /* animate cards when they first become revealed */
  useEffect(() => {
    if (isRevealed && !wasRevealedRef.current) {
      wasRevealedRef.current = true;
      setIsEntering(true);
      const t = setTimeout(() => setIsEntering(false), 600);
      return () => clearTimeout(t);
    }
  }, [isRevealed]);

  /* reset flag when card is removed then re-added (branching) */
  useEffect(() => {
    if (!isRevealed) {
      wasRevealedRef.current = false;
      setIsEntering(false);
    }
  }, [isRevealed]);

  /* subtle scroll when active card enters viewport */
  useEffect(() => {
    if (isActive && cardRef.current) {
      const timer = setTimeout(() => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          window.scrollBy({
            top: rect.bottom - window.innerHeight + 32,
            behavior: "smooth",
          });
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  if (!isRevealed) return null;

  /* ── INFO card (static, no input) ────────────────────────────────────── */
  if (question.type === "info") {
    return (
      <div ref={cardRef} className={isEntering ? "spss-question-enter" : undefined}>
        <InfoCard question={question} />
      </div>
    );
  }

  const InputComponent = INPUT_MAP[question.type];
  const answer = formatAnswer(question, value);
  const hasValue = answer !== null && answer !== "";

  /* ── COMPLETED (summary) view ────────────────────────────────────────── */
  if (!isActive) {
    return (
      <div
        ref={cardRef}
        onClick={() => onEdit(question.id)}
        className={`group relative px-6 py-5 desktop:px-8 desktop:py-5 rounded-2xl border border-white/10
          bg-white/[0.02] cursor-pointer transition-all duration-300
          hover:bg-white/[0.05] hover:border-white/20
          ${isEntering ? "spss-question-enter" : ""}`}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
            <i className="fa-solid fa-check text-[10px] text-white/60" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-white/40 font-sans text-xs mb-1">
              Pertanyaan {questionNumber}
            </p>
            <h3 className="text-white/70 font-sans text-sm desktop:text-base font-medium mb-1">
              {question.question}
            </h3>
            {hasValue && (
              <p className="text-white font-sans text-sm font-semibold truncate">
                {answer}
              </p>
            )}
          </div>

          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-1">
            <i className="fa-solid fa-pen text-white/30 text-xs" />
          </div>
        </div>
      </div>
    );
  }

  /* ── ACTIVE (editable) view ──────────────────────────────────────────── */
  return (
    <div
      ref={cardRef}
      className={`relative px-6 py-8 desktop:px-10 desktop:py-10 rounded-2xl border border-white/15
        bg-white/[0.04] transition-colors duration-300
        ${isEntering ? "spss-question-enter" : ""}`}
    >
      {/* question number badge */}
      <div className="flex items-center gap-3 mb-5">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-midnight text-xs font-bold font-sans">
          {questionNumber}
        </span>
        {question.required && (
          <span className="text-red text-[10px] font-sans font-semibold uppercase tracking-wider">
            Wajib
          </span>
        )}
      </div>

      {/* question text */}
      <h2 className="text-xl desktop:text-2xl font-bold font-sans text-white mb-2">
        {question.question}
      </h2>

      {/* subtext / help text */}
      {question.subtext && (
        <p className="text-sm font-sans text-white/50 mb-6 whitespace-pre-line">
          {question.subtext}
        </p>
      )}

      {/* input */}
      <div className="mt-6">
        {InputComponent && (
          <InputComponent
            question={question}
            value={value}
            onChange={onChange}
            error={error}
          />
        )}
      </div>

      {/* error */}
      {error && (
        <div className="flex items-center gap-2 mt-3">
          <i className="fa-solid fa-circle-exclamation text-red text-xs" />
          <p className="text-red text-xs font-sans font-medium">{error}</p>
        </div>
      )}

      {/* navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
        <button
          type="button"
          onClick={onBack}
          disabled={isFirst}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-sans text-sm font-semibold
            transition-all duration-300
            ${
              isFirst
                ? "text-white/20 cursor-not-allowed"
                : "text-white/60 hover:text-white hover:bg-white/10 cursor-pointer"
            }`}
        >
          <i className="fa-solid fa-arrow-left text-xs" />
          Kembali
        </button>

        <div className="flex gap-3">
          {isLast ? (
            <button
              type="button"
              onClick={onSubmit}
              className="flex items-center gap-2 px-8 py-3 rounded-xl bg-silver text-midnight
                font-sans text-sm font-bold transition-all duration-300
                hover:scale-105 hover:shadow-lg cursor-pointer"
            >
              {submitLabel || "Kirim"}
              <i className="fa-solid fa-arrow-right text-xs" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className="flex items-center gap-2 px-8 py-3 rounded-xl bg-white text-midnight
                font-sans text-sm font-bold transition-all duration-300
                hover:scale-105 hover:shadow-lg cursor-pointer"
            >
              Selanjutnya
              <i className="fa-solid fa-arrow-right text-xs" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default QuestionCard;
